import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Clock, TrendingUp } from "lucide-react";
import axios from "axios";
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";

const API = `${__BACKEND_URL__}/api`;

// Color scale for heatmap
const getHeatColor = (value, max) => {
  if (value === 0) return "bg-slate-100";
  const intensity = Math.min(value / max, 1);
  if (intensity < 0.2) return "bg-emerald-100";
  if (intensity < 0.4) return "bg-emerald-200";
  if (intensity < 0.6) return "bg-emerald-300";
  if (intensity < 0.8) return "bg-emerald-400";
  return "bg-emerald-500";
};

const getTextColor = (value, max) => {
  if (value === 0) return "text-slate-400";
  const intensity = Math.min(value / max, 1);
  if (intensity < 0.6) return "text-slate-700";
  return "text-white";
};

export const SalesHeatmap = ({ 
  storeId = null,
  storeIds = null,
  days = 30,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState("count"); // "count" or "total"

  useEffect(() => {
    loadHeatmapData();
  }, [storeId, storeIds, days]);

  const loadHeatmapData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (storeIds && storeIds.length > 0) {
        params.append("store_ids", storeIds.join(","));
      } else if (storeId) {
        params.append("store_id", storeId);
      }
      params.append("days", days.toString());
      
      const response = await axios.get(`${API}/sales/v4/hourly-heatmap?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error("Error loading heatmap:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  if (loading) {
    return (
      <Card className="shadow-sm border-slate-200">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="ml-2 text-slate-500">Carregando mapa de calor...</span>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.heatmap) {
    return (
      <Card className="shadow-sm border-slate-200">
        <CardContent className="text-center py-12 text-slate-400">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Sem dados de horário disponíveis</p>
          <p className="text-sm mt-1">Importe vendas do banco de dados para ver o mapa de calor</p>
        </CardContent>
      </Card>
    );
  }

  // Process heatmap data for display
  const dayNames = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Filter to business hours (6am - 11pm)
  const businessHours = hours.filter(h => h >= 6 && h <= 23);
  
  // Get max value for color scaling
  const maxValue = viewMode === "count" 
    ? Math.max(...data.heatmap.map(d => d.count), 1)
    : Math.max(...data.heatmap.map(d => d.total), 1);

  // Create grid data
  const getCell = (day, hour) => {
    const cell = data.heatmap.find(d => d.day === day && d.hour === hour);
    return cell || { count: 0, total: 0 };
  };

  return (
    <Card className="shadow-sm border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-600" />
            Mapa de Calor - Vendas por Horário
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === "count" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("count")}
              className="h-7 text-xs"
            >
              Pedidos
            </Button>
            <Button
              variant={viewMode === "total" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("total")}
              className="h-7 text-xs"
            >
              Faturamento
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-500">
          Últimos {days} dias | {data.total_orders} pedidos | {formatCurrency(data.total_revenue)}
        </p>
      </CardHeader>
      
      <CardContent>
        {/* Heatmap Grid */}
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Hour labels */}
            <div className="flex mb-1">
              <div className="w-10 flex-shrink-0"></div>
              {businessHours.map(hour => (
                <div key={hour} className="flex-1 text-center text-[10px] text-slate-500">
                  {hour}h
                </div>
              ))}
            </div>
            
            {/* Grid rows */}
            {[0, 1, 2, 3, 4, 5, 6].map(day => (
              <div key={day} className="flex items-center mb-1">
                <div className="w-10 flex-shrink-0 text-xs font-medium text-slate-600">
                  {dayNames[day]}
                </div>
                {businessHours.map(hour => {
                  const cell = getCell(day, hour);
                  const value = viewMode === "count" ? cell.count : cell.total;
                  const displayValue = viewMode === "count" 
                    ? (cell.count || "") 
                    : (cell.total > 0 ? `${Math.round(cell.total)}` : "");
                  
                  return (
                    <div
                      key={`${day}-${hour}`}
                      className={`
                        flex-1 h-8 flex items-center justify-center mx-0.5 rounded
                        text-[10px] font-medium cursor-default transition-all
                        hover:ring-2 hover:ring-emerald-400 hover:ring-offset-1
                        ${getHeatColor(value, maxValue)}
                        ${getTextColor(value, maxValue)}
                      `}
                      title={`${dayNames[day]} ${hour}:00 - ${cell.count} pedidos, ${formatCurrency(cell.total)}`}
                    >
                      {displayValue}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
          <span>Menor</span>
          <div className="flex gap-0.5">
            <div className="w-4 h-4 rounded bg-slate-100"></div>
            <div className="w-4 h-4 rounded bg-emerald-100"></div>
            <div className="w-4 h-4 rounded bg-emerald-200"></div>
            <div className="w-4 h-4 rounded bg-emerald-300"></div>
            <div className="w-4 h-4 rounded bg-emerald-400"></div>
            <div className="w-4 h-4 rounded bg-emerald-500"></div>
          </div>
          <span>Maior</span>
        </div>
        
        {/* Peak Hours */}
        {data.peak_hours && data.peak_hours.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              Horários de Pico
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {data.peak_hours.slice(0, 5).map((peak, idx) => (
                <div 
                  key={idx} 
                  className="p-2 bg-gradient-to-br from-emerald-50 to-white rounded-lg border border-emerald-200"
                >
                  <p className="text-xs font-medium text-emerald-800">
                    {peak.day_name} {peak.hour_label}
                  </p>
                  <p className="text-sm font-bold text-emerald-600">
                    {peak.count} pedidos
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {formatCurrency(peak.total)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Daily Summary */}
        {data.daily_totals && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Resumo por Dia da Semana</h4>
            <div className="grid grid-cols-7 gap-1">
              {data.daily_totals.map((day, idx) => (
                <div 
                  key={idx} 
                  className={`p-2 rounded-lg text-center ${
                    day.total_orders > 0 ? 'bg-slate-50' : 'bg-slate-100/50'
                  }`}
                >
                  <p className="text-xs font-medium text-slate-600">{day.day_name}</p>
                  <p className="text-sm font-bold text-slate-800">{day.total_orders}</p>
                  <p className="text-[10px] text-slate-500">
                    {day.total_revenue > 0 ? formatCurrency(day.total_revenue) : '-'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesHeatmap;
